import React from "react";

type ClientCard = {
  label?: string;
  name?: string;
  description?: string;
};

type SkewCard = {
  title?: string;
  description?: string;
};

type CaseStudiesSkewSatusCardsSectionProps = {
  clients?: ClientCard[];
  skewCards?: SkewCard[];
  clientIntroPara?: string;
};

const CaseStudiesSkewSatusCardsSection: React.FC<
  CaseStudiesSkewSatusCardsSectionProps
> = ({ clients = [], skewCards = [], clientIntroPara = "" }) => {
  const upperClients = clients.slice(0, 2);
  const lowerClients = clients.slice(2);

  return (
    <section className="2xl:mt-12 xl:section-background-filler relative overflow-hidden 2xl:max-h-[720px]">
      <div className="relative custom-container mx-auto h-full overflow-hidden">
        <div className="mx-auto lg:grid lg:grid-cols-12 items-center gap-12 2xl:gap-32">

          {/* LEFT COLUMN */}
          {clients.length > 0 && (
            <div className="lg:col-span-6 flex items-stretch">
              <div className="flex w-full py-4 text-center lg:text-left lg:py-[140px] flex-col gap-8">
                <p className="text-lg font-medium text-[#272d57]">
                  {clientIntroPara}
                </p>

                {/* Top two cards */}
                {upperClients.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 xl:gap-6">
                    {upperClients.map((client, idx) => (
                      <div
                        key={idx}
                        className="px-6 py-2 shadow-[0_4px_64px_rgba(0,0,0,0.06)]"
                      >
                        {client.label && (
                          <span className="text-sm text-[#d53333] uppercase font-semibold">
                            {client.label}
                          </span>
                        )}
                        {client.name && (
                          <h4 className="text-base 2xl:text-xl text-[#272d57]">
                            {client.name}
                          </h4>
                        )}
                        {client.description && (
                          <p className="text-base 2xl:text-lg sm:font-semibold capitalize text-[#272d57]">
                            {client.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Remaining cards */}
                {lowerClients.length > 0 && (
                  <div className="flex flex-col gap-4 xl:gap-6">
                    {lowerClients.map((client, idx) => (
                      <div
                        key={idx}
                        className="px-6 py-2 shadow-[0_4px_64px_rgba(0,0,0,0.06)]"
                      >
                        {client.label && (
                          <span className="text-sm text-[#d53333] uppercase font-semibold">
                            {client.label}
                          </span>
                        )}
                        {client.name && (
                          <h4 className="text-base 2xl:text-xl text-[#272d57]">
                            {client.name}
                          </h4>
                        )}
                        {client.description && (
                          <p className="text-sm 2xl:text-lg text-[#272d57] capitalize">
                            {client.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* RIGHT COLUMN */}
          {skewCards.length > 0 && (
            <div className="lg:col-span-6 flex items-stretch relative overflow-visible">
              <div className="2xl:-ml-10 w-full">
                {/* 🔥 Skew only on 2xl */}
                <div className="transform 2xl:-skew-x-20 lg:px-10">
                  <div className="grid grid-cols-2 gap-0">
                    {skewCards.map((card, idx) => {
                      const isLastOdd =
                        skewCards.length % 2 !== 0 &&
                        idx === skewCards.length - 1;

                      return (
                        <div
                          key={idx}
                          className={`flex flex-col justify-center items-center text-center p-6 transition-all duration-300
                            ${idx === 0
                              ? "section-background-filler-2 border-r border-[#EDEDED]"
                              : idx === 3
                              ? "section-background-filler-2 border-l border-[#EDEDED]"
                              : ""}
                            ${
                              isLastOdd
                                ? "col-span-2 py-6 section-background-filler-2"
                                : "h-auto 2xl:h-[200px]"
                            }
                          `}
                        >
                          {card.title && (
                            <h4 className="transform 2xl:skew-x-20 mb-1 text-2xl  font-semibold text-[#272d57]">
                              {card.title}
                            </h4>
                          )}
                          {card.description && (
                            <p className="transform 2xl:skew-x-20 sm:max-w-xs text-sm capitalize text-[#272d57]">
                              {card.description}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </section>
  );
};

export default CaseStudiesSkewSatusCardsSection;
